// How a person reads a time and a date.
//
// The app STORES a single appointment time as "13:30" so the scheduling maths is exact, but nobody
// in the office says "thirteen thirty". Every screen a person reads has to say "1:30 PM" — the same
// words the customer gets in the text — so the office and the customer never see two different
// things. This is the one helper every screen uses, so it cannot drift apart again.
//
// A time WINDOW the office wrote ("2-4pm", "between 8 and 10") is the promise the office made, so it
// is kept exactly as written and is never turned into a single clock time.
//
// Nothing here is used for calculation. Scheduling maths uses quietHours.ts, which reads the clock
// in 24-hour form on purpose.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * "13:30" -> "1:30 PM", "09:00" -> "9:00 AM", "00:15" -> "12:15 AM".
 * A window ("2-4pm"), a blank, or anything we cannot read comes back exactly as it was.
 */
export function clockWords(value: string | null | undefined): string {
  if (value == null) return '';
  const s = String(value).trim();
  // "08:00" and Postgres's "08:00:00" both count as one time
  const m = /^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?$/.exec(s);
  if (!m) return s;
  const hour = Number(m[1]);
  if (hour > 23) return s;
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${m[2]} ${hour < 12 ? 'AM' : 'PM'}`;
}

/** "6:00 PM" from the hour and minute of a clock. Out-of-range numbers come back as digits. */
export function clockWordsFrom(hour: number, minute: number): string {
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return '';
  return clockWords(`${String(Math.trunc(hour)).padStart(2, '0')}:${String(Math.trunc(minute)).padStart(2, '0')}`);
}

/**
 * "2026-09-09" -> "Wed, Sep 9". A blank, or anything that is not a plain calendar day, comes back
 * exactly as it was. The day is read as a calendar day, never shifted by anyone's timezone.
 */
export function dateWords(value: string | null | undefined): string {
  if (value == null) return '';
  const s = String(value).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(d.getTime()) || d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return s;
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[month - 1]} ${day}`;
}
